import { db } from '../firebase/config';
import { doc, setDoc, getDoc, collection, addDoc } from 'firebase/firestore';

/**
 * Creates a new workspace and migrates data
 * @param {string} workspaceName - Name of the workspace
 * @param {Array<string>} memberIds - Array of user IDs who have access
 * @param {Object} data - Data to migrate
 * @returns {Promise<string>} - ID of the created workspace
 */
export const createWorkspace = async (workspaceName, memberIds, data = null) => {
  try {
    // Create workspace document
    const workspaceRef = collection(db, 'workspaces');
    const workspaceDoc = await addDoc(workspaceRef, {
      name: workspaceName,
      members: memberIds,
      createdAt: new Date(),
      lastUpdated: new Date()
    });

    // If we have data to migrate, create the bills collection
    if (data) {
      const billsRef = doc(db, `workspaces/${workspaceDoc.id}/data/bills`);
      await setDoc(billsRef, {
        bills: data.bills || [],
        lastUpdated: new Date()
      });

      // Create history collection
      const historyRef = doc(db, `workspaces/${workspaceDoc.id}/data/history`);
      await setDoc(historyRef, {
        history: data.history || {},
        lastUpdated: new Date()
      });

      // Create settings collection (customProfitTax)
      const settingsRef = doc(db, `workspaces/${workspaceDoc.id}/data/settings`);
      await setDoc(settingsRef, {
        customProfitTax: data.customProfitTax || {},
        lastUpdated: new Date()
      });

      // Create purchases collection
      const purchasesRef = doc(db, `workspaces/${workspaceDoc.id}/data/purchases`);
      await setDoc(purchasesRef, {
        purchases: data.purchases || {},
        lastUpdated: new Date()
      });

      // Create skippedBills collection
      const skippedRef = doc(db, `workspaces/${workspaceDoc.id}/data/skippedBills`);
      await setDoc(skippedRef, {
        skippedBills: data.skippedBills || {},
        lastUpdated: new Date()
      });
    }

    return workspaceDoc.id;
  } catch (error) {
    console.error('Error creating workspace:', error);
    throw error;
  }
};

/**
 * Migrates user data to a workspace
 * @param {string} userId - User ID to migrate data from
 * @param {string} workspaceName - Name for the new workspace
 * @param {Array<string>} memberIds - Array of user IDs who should have access
 * @returns {Promise<Object>} Migration result
 */
export const migrateUserDataToWorkspace = async (userId, workspaceName, memberIds) => {
  try {
    // Get user data
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User data not found');
    }

    const userData = userDoc.data();
    
    // Create new workspace with data
    const workspaceId = await createWorkspace(workspaceName, memberIds, userData);

    return {
      success: true,
      workspaceId,
      message: 'Data successfully migrated to new workspace'
    };
  } catch (error) {
    console.error('Migration error:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Validates and fixes workspace data structure
 * @param {string} workspaceId - Workspace ID to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateWorkspaceStructure = async (workspaceId) => {
  try {
    const results = [];
    
    // Check main workspace document
    const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));
    if (!workspaceDoc.exists()) {
      throw new Error('Workspace not found');
    }
    
    results.push({ collection: 'workspace', exists: true, data: workspaceDoc.data() });
    
    // Check required sub-collections
    const requiredCollections = ['bills', 'history', 'settings', 'purchases', 'skippedBills'];
    
    for (const collectionName of requiredCollections) {
      const docRef = doc(db, `workspaces/${workspaceId}/data/${collectionName}`);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        // Create missing collection with empty data
        const emptyData = {
          lastUpdated: new Date()
        };
        
        switch (collectionName) {
          case 'bills':
            emptyData.bills = [];
            break;
          case 'history':
            emptyData.history = {};
            break;
          case 'settings':
            emptyData.customProfitTax = {};
            break;
          case 'purchases':
            emptyData.purchases = {};
            break;
          case 'skippedBills':
            emptyData.skippedBills = {};
            break;
        }
        
        await setDoc(docRef, emptyData);
        results.push({ collection: collectionName, exists: false, created: true });
      } else {
        results.push({ collection: collectionName, exists: true, data: docSnap.data() });
      }
    }
    
    return {
      success: true,
      results,
      message: 'Workspace structure validated and fixed'
    };
  } catch (error) {
    console.error('Validation error:', error);
    return {
      success: false,
      message: error.message
    };
  }
}; 